import { Check, Minus } from "lucide-react";
import { useId, useRef, type ComponentProps, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../util";
import { useFieldControl } from "./Field";

export interface CheckboxProps extends Omit<ComponentProps<"input">, "type" | "onChange" | "size"> {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  indeterminate?: boolean;
}

export function Checkbox({ checked, onChange, label, description, indeterminate, className, disabled, ...rest }: CheckboxProps) {
  const auto = useId();
  const f = useFieldControl(rest);
  const id = f.id ?? `gf-cb${auto}`;
  const descId = description ? `${id}-desc` : undefined;
  return (
    <label className={cx("gf-check", disabled && "gf-check--disabled", className)} htmlFor={id}>
      <span className="gf-check__box">
        <input
          {...rest}
          id={id}
          type="checkbox"
          className="gf-check__input"
          checked={checked}
          disabled={disabled}
          aria-describedby={[descId, f["aria-describedby"]].filter(Boolean).join(" ") || undefined}
          ref={(el) => {
            if (el) el.indeterminate = !!indeterminate;
          }}
          onChange={(e) => onChange?.(e.currentTarget.checked)}
        />
        <span className="gf-check__mark" aria-hidden="true">
          {indeterminate ? <Minus size={12} strokeWidth={3} /> : <Check size={12} strokeWidth={3} />}
        </span>
      </span>
      {label || description ? (
        <span className="gf-check__text">
          {label ? <span className="gf-check__label">{label}</span> : null}
          {description ? (
            <span className="gf-check__desc" id={descId}>
              {description}
            </span>
          ) : null}
        </span>
      ) : null}
    </label>
  );
}

export interface SwitchProps extends Omit<ComponentProps<"button">, "onChange" | "children" | "value"> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Put the switch after the text (settings rows). */
  labelPosition?: "start" | "end";
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  size = "md",
  labelPosition = "end",
  className,
  disabled,
  id,
  ...rest
}: SwitchProps) {
  const auto = useId();
  const switchId = id ?? `gf-sw${auto}`;
  const labelId = label ? `${switchId}-label` : undefined;
  const descId = description ? `${switchId}-desc` : undefined;
  const control = (
    <button
      {...rest}
      id={switchId}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={rest["aria-label"] ? undefined : labelId}
      aria-describedby={descId}
      disabled={disabled}
      className={cx("gf-switch", `gf-switch--${size}`, checked && "gf-switch--on")}
      onClick={() => onChange(!checked)}
    >
      <span className="gf-switch__thumb" />
    </button>
  );
  if (!label && !description) return <span className={className}>{control}</span>;
  const text = (
    <span className="gf-switchrow__text">
      {label ? (
        <span className="gf-switchrow__label" id={labelId} onClick={() => !disabled && onChange(!checked)}>
          {label}
        </span>
      ) : null}
      {description ? (
        <span className="gf-switchrow__desc" id={descId}>
          {description}
        </span>
      ) : null}
    </span>
  );
  return (
    <div className={cx("gf-switchrow", labelPosition === "start" && "gf-switchrow--start", disabled && "gf-switchrow--disabled", className)}>
      {labelPosition === "start" ? (
        <>
          {text}
          {control}
        </>
      ) : (
        <>
          {control}
          {text}
        </>
      )}
    </div>
  );
}

export interface SegmentedOption<V extends string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<V extends string> {
  options: ReadonlyArray<SegmentedOption<V>>;
  value: V;
  onChange: (value: V) => void;
  /** Accessible name for the group. */
  label: string;
  size?: "sm" | "md";
  className?: string;
  block?: boolean;
}

/** Radio-group semantics, arrow keys move + select. */
export function SegmentedControl<V extends string>({ options, value, onChange, label, size = "md", className, block }: SegmentedControlProps<V>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options.filter((o) => !o.disabled);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"];
    if (!keys.includes(e.key) || enabled.length === 0) return;
    e.preventDefault();
    const idx = enabled.findIndex((o) => o.value === value);
    let next = idx;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (idx + 1) % enabled.length;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (idx - 1 + enabled.length) % enabled.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = enabled.length - 1;
    const opt = enabled[next];
    if (!opt) return;
    onChange(opt.value);
    const i = options.findIndex((o) => o.value === opt.value);
    refs.current[i]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx("gf-seg", `gf-seg--${size}`, block && "gf-seg--block", className)}
      onKeyDown={onKeyDown}
    >
      {options.map((o, i) => {
        const active = o.value === value;
        const hasActive = options.some((x) => x.value === value);
        const tabbable = active || (!hasActive && o.value === enabled[0]?.value);
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={tabbable ? 0 : -1}
            disabled={o.disabled}
            className={cx("gf-seg__opt", active && "gf-seg__opt--active")}
            onClick={() => onChange(o.value)}
          >
            {o.icon ? <span className="gf-seg__icon">{o.icon}</span> : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
